import { AuthContext } from "@/contexts/AuthContext";
import { useLogger } from "@/hooks/useLogger";
import Userback, {
	type UserbackWidget,
	type UserbackOptions,
} from "@userback/widget";
import {
	createContext,
	useContext,
	useEffect,
	useState,
	type ReactNode,
} from "react";

interface UserbackContextType {
	userback: UserbackWidget | null;
	isLoaded: boolean;
}

const UserbackContext = createContext<UserbackContextType | undefined>(
	undefined,
);

interface UserbackProviderProps {
	children: ReactNode;
}

export const UserbackProvider = ({ children }: UserbackProviderProps) => {
	const logger = useLogger();
	const [userback, setUserback] = useState<UserbackWidget | null>(null);
	const [isLoaded, setIsLoaded] = useState(false);
	const authContext = useContext(AuthContext);

	if (!authContext) {
		throw new Error("UserbackProvider must be used within an AuthProvider");
	}

	const { user, isAuthenticated } = authContext;

	useEffect(() => {
		const initUserback = async () => {
			try {
				const environment = import.meta.env.VITE_APP_ENV;
				const accessToken = import.meta.env.VITE_USERBACK_ACCESS_TOKEN;

				// Only initialize Userback in production environment
				if (environment !== "production") {
					logger.info(
						"Userback: Skipping initialization (not in production environment)",
					);
					setIsLoaded(true);
					return;
				}

				if (!accessToken) {
					logger.warn(
						"Userback: Access token not found in environment variables",
					);
					setIsLoaded(true);
					return;
				}

				const options: UserbackOptions = {};

				// Add user data if authenticated
				if (isAuthenticated && user) {
					options.user_data = {
						id: user.uuid || user.id,
						info: {
							name:
								user.name ||
								`${user.first_name || ""} ${user.last_name || ""}`.trim(),
							email: user.email,
						},
					};
				}

				const instance = await Userback(accessToken, options);
				setUserback(instance);
				setIsLoaded(true);

				logger.info("Userback: Successfully initialized");
			} catch (error) {
				logger.error("Userback: Failed to initialize", error);
				setIsLoaded(true); // Mark as loaded even on error to prevent infinite loading
			}
		};

		initUserback();
		// Only run once on mount - we don't want to reinitialize when user/auth changes
		// User identification is handled separately in the second useEffect
	}, []);

	// Update user data when authentication state changes
	useEffect(() => {
		if (userback && isAuthenticated && user) {
			try {
				const userId = String(user.uuid || user.id);
				const userInfo = {
					name:
						user.name ||
						`${user.first_name || ""} ${user.last_name || ""}`.trim(),
					email: user.email,
				};
				userback.identify(userId, userInfo);
				logger.info("Userback: User identified");
			} catch (error) {
				logger.error("Userback: Failed to identify user", error);
			}
		}
	}, [userback, isAuthenticated, user]);

	const value: UserbackContextType = {
		userback,
		isLoaded,
	};

	return (
		<UserbackContext.Provider value={value}>
			{children}
		</UserbackContext.Provider>
	);
};

export const useUserback = () => {
	const context = useContext(UserbackContext);
	if (context === undefined) {
		throw new Error("useUserback must be used within a UserbackProvider");
	}
	return context;
};
